import { useEffect, useState } from "react";
import {
  Sun, Bathtub, Books, CookingPot, Bed, Couch, Desk, Tree, Plant, Door, Armchair, Garage, Flower, Cactus, Snowflake, Lamp, House, MapPin, Fish, Television, Stairs,
  Leaf, Drop, Mountains, Umbrella, Buildings, Warehouse, Tent, Basket, Coffee, Bird, Butterfly, Bug, Cloud, Moon, Star, Heart, Sparkle, Lightbulb, Wind, Thermometer,
  PottedPlant, Shower, Campfire, Barn, Storefront, FlowerLotus, FlowerTulip, Clover, TreePalm, TreeEvergreen, SunHorizon, Waves, Package, Archive, Table, Chair,
  OfficeChair, Briefcase, PawPrint, Cat, Dog, MusicNotes, Heartbeat, Recycle, Car, Fire, Rainbow, Feather, Carrot, Pepper, Grains, Acorn, Bell, Gift, Trophy, Crown, SprayBottle,
} from "@phosphor-icons/react";
import { GraveIcon } from "../components/GraveIcon";
import { getLocationIcons, setLocationIcon } from "./api";

export const LOCATION_ICONS = {
  window: [Sun, "Window"], graveyard: [GraveIcon, "Graveyard"], bathroom: [Bathtub, "Bathroom"], shelf: [Books, "Shelf"], kitchen: [CookingPot, "Kitchen"], bedroom: [Bed, "Bedroom"],
  living: [Couch, "Living room"], office: [Desk, "Office"], outdoor: [Tree, "Outdoors"], greenhouse: [Plant, "Greenhouse"], entry: [Door, "Entry"],
  nook: [Armchair, "Nook"], garage: [Garage, "Garage"], garden: [Flower, "Garden"], desert: [Cactus, "Sunny dry spot"], cold: [Snowflake, "Cool room"],
  growlight: [Lamp, "Grow light"], porch: [House, "Porch"], aquarium: [Fish, "Aquarium"], tv: [Television, "TV stand"], stairs: [Stairs, "Stairs"],
  leaf: [Leaf, "Leaf"], potted: [PottedPlant, "Potted plant"], mister: [SprayBottle, "Misting spot"], tulip: [FlowerTulip, "Tulip"], lotus: [FlowerLotus, "Lotus"], clover: [Clover, "Clover"],
  palm: [TreePalm, "Palm"], evergreen: [TreeEvergreen, "Evergreen"], acorn: [Acorn, "Acorn"], feather: [Feather, "Fern"], carrot: [Carrot, "Veg patch"], pepper: [Pepper, "Peppers"],
  grains: [Grains, "Grasses"], drop: [Drop, "Humid spot"], shower: [Shower, "Shower"], waves: [Waves, "Water feature"], cloud: [Cloud, "Shade"], sunrise: [SunHorizon, "East facing"],
  moon: [Moon, "Low light"], thermometer: [Thermometer, "Warm room"], wind: [Wind, "Breezy"], fire: [Fire, "Fireplace"], bulb: [Lightbulb, "Lamp light"], rainbow: [Rainbow, "Colorful"],
  sparkle: [Sparkle, "Special"], star: [Star, "Favorites"], heart: [Heart, "Loved"], crown: [Crown, "Showpieces"], trophy: [Trophy, "Prize plants"], gift: [Gift, "Gifts"],
  mountains: [Mountains, "Rockery"], umbrella: [Umbrella, "Covered"], tent: [Tent, "Grow tent"], campfire: [Campfire, "Fire pit"], barn: [Barn, "Barn"], shed: [Warehouse, "Shed"],
  buildings: [Buildings, "Building"], shop: [Storefront, "Shop"], table: [Table, "Table"], chair: [Chair, "Chair"], officechair: [OfficeChair, "Desk chair"], work: [Briefcase, "Work"],
  coffee: [Coffee, "Coffee table"], music: [MusicNotes, "Music room"], basket: [Basket, "Hanging basket"], box: [Package, "Storage"], archive: [Archive, "Cabinet"], car: [Car, "Car"],
  bird: [Bird, "Bird feeder"], butterfly: [Butterfly, "Pollinators"], bug: [Bug, "Quarantine"], health: [Heartbeat, "Recovery"], recycle: [Recycle, "Compost"], bell: [Bell, "Check often"],
  pets: [PawPrint, "Pet safe"], cat: [Cat, "Cat zone"], dog: [Dog, "Dog zone"], other: [MapPin, "Other"],
};

const RULES = [
  [/grave|cemetery|memorial/, "graveyard"],
  [/window|sill|sunroom|skylight/, "window"], [/bath|shower/, "bathroom"], [/shelf|shelves|bookcase|cabinet/, "shelf"], [/kitchen|counter/, "kitchen"],
  [/bed/, "bedroom"], [/living|lounge|family room|den/, "living"], [/office|desk|study/, "office"], [/greenhouse|conservatory/, "greenhouse"],
  [/porch|patio|deck|balcony|veranda/, "porch"], [/garden|bed outside|planter/, "garden"], [/yard|outdoor|outside|backyard/, "outdoor"],
  [/entry|hall|foyer|door/, "entry"], [/garage|basement/, "garage"], [/grow|tent|light/, "growlight"], [/tank|aquarium|terrarium|paludarium/, "aquarium"],
  [/stair|landing/, "stairs"], [/tv|media/, "tv"], [/nook|corner|chair/, "nook"],
];

export const autoIconKey = (name = "") => (RULES.find(([re]) => re.test(name.toLowerCase())) || [null, "other"])[1];

let cache = null;
const subs = new Set();
export function useLocationIcons() {
  const [map, setMap] = useState(cache || {});
  useEffect(() => {
    subs.add(setMap);
    if (!cache) getLocationIcons().then((m) => { cache = m; subs.forEach((f) => f(m)); }).catch(() => {});
    return () => subs.delete(setMap);
  }, []);
  const update = async (name, icon) => {
    await setLocationIcon(name, icon);
    cache = { ...(cache || {}), [name]: icon };
    if (!icon) delete cache[name];
    subs.forEach((f) => f(cache));
  };
  return [map, update];
}

export const iconKeyFor = (name, map) => (map[name] && LOCATION_ICONS[map[name]] ? map[name] : autoIconKey(name));

export function LocationIcon({ name, map, className = "h-4 w-4", weight = "duotone" }) {
  const Icon = LOCATION_ICONS[iconKeyFor(name, map || {})][0];
  return <Icon weight={weight} className={className} />;
}
